import React from 'react'
import { FormattedMessage, defineMessages } from 'react-intl'
import { isEmpty } from 'lodash'
import { useSelector } from 'react-redux'
import classnames from 'classnames'

// Selectors
import { selectHomeValue } from 'src/store/selectors/home/metadata'
import {
  selectContactFirstName,
  selectContactPhotoUri,
} from 'src/store/selectors/contact'
import { selectShouldRenderEquitySmartSearchFeatures } from 'src/store/selectors/display'
import {
  selectTotalEquity,
  selectHowIsThisCalculatedData,
} from 'src/store/selectors/home/equity'

// Utils
import { equitySearchTable } from 'src/constants/equity'
import { SYSTEM_REQUEST } from 'src/constants/messages'
import { EQUITY_SMART_SEARCH_TOPIC_KEY } from 'src/constants/topicKeys'
import { useFormatMessage } from 'src/providers/intl'

// Components
import { Page } from 'src/Components'
import {
  IconMinus,
  IconTimes,
  IconPlus,
  IconTilde,
} from 'src/Components/shared/Icon'
import Format from 'src/Components/shared/Format'
import Question from 'src/Components/shared/Questions/QuestionSingleContainer'
import Text from 'src/Components/shared/Text'
import { RedirectToHomeDigest } from 'src/Components/shared/Navigation'
import BotLogo from 'src/Components/shared/Logo/BotLogo'

// Styles
import styles from './EquitySmartSearchCalculation.module.scss'

const message = defineMessages({
  placeholder: {
    id: 'MarketSpotLight.equitySmartSearchCalculation.questionPlaceholder',
    defaultMessage: 'Hey {firstName}, could you walk me through this?',
  },
})

const EquitySmartSearchCalculation = () => {
  const TWENTY_PERCENT = equitySearchTable.TWENTY_PERCENT_DOWN

  const avatarUri = useSelector(selectContactPhotoUri)
  const data = useSelector(selectHowIsThisCalculatedData)
  const equity = Math.round(useSelector(selectTotalEquity))
  const firstName = useSelector(selectContactFirstName)
  const homeValue = useSelector(selectHomeValue)
  const intlFormat = useFormatMessage()
  const showEquitySmartSearchFeatures = useSelector(
    selectShouldRenderEquitySmartSearchFeatures
  )

  if (!showEquitySmartSearchFeatures || isEmpty(data)) {
    return <RedirectToHomeDigest />
  }

  const {
    cashAfterSelling,
    downPayment,
    loanBalance,
    mortgagePaymentDifference,
    percentDown,
    pocketAmount,
    purchasePrice,
  } = data

  const percentDownAsWholeNumber = Math.round(percentDown * 100)

  return (
    <Page>
      <Page.Body>
        <div
          className={styles.botLogo}
          data-qa='equity-smart-search-table-calculation'
        >
          <BotLogo />
        </div>
        <ul className={styles.list}>
          {/* FIRST LIST ITEM */}
          <li className={styles.listItem}>
            {/* First Blurb */}
            <div className={styles.listHeading}>
              <Text
                element='div'
                size='l'
                align='left'
                variant='neutralCool'
                variantCount='900'
              >
                <FormattedMessage
                  id='MarketSpotLight.equitySmartSearchCalculation.cta'
                  defaultMessage="If you sold right now for {homeValue}, you'd have around {cashAfterSelling} in cash"
                  values={{
                    homeValue: (
                      <Text element='span' variant='primary'>
                        <Format.Currency value={homeValue} abbreviate />
                      </Text>
                    ),
                    cashAfterSelling: (
                      <Text element='span' variant='success' variantCount='700'>
                        <Format.Currency value={cashAfterSelling} abbreviate />
                      </Text>
                    ),
                  }}
                />
              </Text>
            </div>
            {/* First Blurb Calculation - Cash After Selling */}
            <div
              className={classnames(
                styles.listHeading,
                styles.listHeadingPanel
              )}
            >
              <div className={styles.row}>
                <div className={classnames(styles.column, styles.column1)} />
                <div className={classnames(styles.column, styles.column2)}>
                  <Text element='span' variant='primary'>
                    <Format.Currency value={equity} />
                  </Text>
                </div>
                <div className={classnames(styles.column, styles.column3)}>
                  <Text variant='neutralCool' variantCount='700'>
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearchCalculation.yourEquity'
                      defaultMessage='Your equity'
                    />
                  </Text>
                </div>
              </div>
              <div className={styles.row}>
                <div className={classnames(styles.column, styles.column1)}>
                  <IconMinus variant='neutralCool' variantCount='700' />
                </div>
                <div className={classnames(styles.column, styles.column2)}>
                  <Text element='span' variant='neutralCool' variantCount='700'>
                    <Format.Currency value={equity - cashAfterSelling} />
                  </Text>
                </div>
                <div className={classnames(styles.column, styles.column3)}>
                  <Text variant='neutralCool' variantCount='700'>
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearchCalculation.sellingFees'
                      defaultMessage='Selling fees ({commissionPercent}%)'
                      values={{
                        commissionPercent: Math.round(
                          equitySearchTable.SELLING_COST_PERCENT * 100
                        ),
                      }}
                    />
                  </Text>
                </div>
              </div>
              <div className={classnames(styles.row, styles.border)}>
                <div className={classnames(styles.column, styles.column1)} />
                <div className={classnames(styles.column, styles.column2)}>
                  <Text element='span' variant='success' variantCount='700'>
                    <Format.Currency value={cashAfterSelling} />
                  </Text>
                </div>
                <div className={classnames(styles.column, styles.column3)}>
                  <Text variant='neutralCool' variantCount='700'>
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearchCalculation.cash'
                      defaultMessage='Cash'
                    />
                  </Text>
                </div>
              </div>
            </div>
          </li>

          {/* SECOND LIST ITEM */}
          <li className={styles.listItem}>
            {/* Second Blurb */}
            <div className={styles.listHeading}>
              <Text
                element='div'
                size='l'
                align='left'
                variant='neutralCool'
                variantCount='900'
              >
                <FormattedMessage
                  id='MarketSpotLight.equitySmartSearchCalculation.downPaymentCta'
                  defaultMessage='Then you could pocket some cash and use the remaining as a down payment on a new house'
                />
              </Text>
            </div>
            {/* Second Blurb Calculation - New Purchase Price */}
            <div
              className={classnames(
                styles.listHeading,
                styles.listHeadingPanel
              )}
            >
              <div className={styles.row}>
                <div className={classnames(styles.column, styles.column1)} />
                <div className={classnames(styles.column, styles.column2)}>
                  <Text element='span' variant='success' variantCount='700'>
                    <Format.Currency value={cashAfterSelling} />
                  </Text>
                </div>
                <div className={classnames(styles.column, styles.column3)}>
                  <Text variant='neutralCool' variantCount='700'>
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearchCalculation.cash'
                      defaultMessage='Cash'
                    />
                  </Text>
                </div>
              </div>
              <div className={styles.row}>
                <div className={classnames(styles.column, styles.column1)}>
                  <Text
                    variant='neutralCool'
                    variantCount='700'
                    element='span'
                    size='xxs'
                    weight='semibold'
                  >
                    <IconMinus variant='neutralCool' variantCount='700' />
                  </Text>
                </div>
                <div className={classnames(styles.column, styles.column2)}>
                  <div className={styles.pocketAmountWrapper}>
                    <div className={styles.pocketAmountBox}>
                      <Text
                        variant='success'
                        variantCount='800'
                        size='tiny'
                        element='div'
                        className={styles.pocketAmountText}
                      >
                        <FormattedMessage
                          id='MarketSpotLight.equitySmartSearch.pocketPlus'
                          defaultMessage='+POCKET'
                        />
                      </Text>
                      <Text
                        element='div'
                        variant='success'
                        variantCount='700'
                        size='xl'
                        className={styles.pocketAmountAmount}
                      >
                        <div className={styles.tildeWrapper}>
                          <span
                            className={styles.pocketTilde}
                            aria-hidden='true'
                            title='about'
                          >
                            <IconTilde />
                          </span>
                          <span className={styles.srOnly}>about</span>
                          <Format.Currency value={pocketAmount} abbreviate />
                        </div>
                      </Text>
                    </div>
                  </div>
                </div>
                <div className={classnames(styles.column, styles.column3)}>
                  <Text variant='neutralCool' variantCount='700' element='span'>
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearchCalculation.pocketedCash'
                      defaultMessage='Pocketed cash'
                    />
                  </Text>
                </div>
              </div>
              <div className={classnames(styles.row, styles.border)}>
                <div className={classnames(styles.column, styles.column1)} />
                <div className={classnames(styles.column, styles.column2)}>
                  <Text element='span' variant='primary'>
                    <Format.Currency value={downPayment} />
                  </Text>
                </div>
                <div className={classnames(styles.column, styles.column3)}>
                  <Text
                    variant='neutralCool'
                    variantCount='700'
                    element='span'
                    align='left'
                  >
                    {percentDown === TWENTY_PERCENT && (
                      <FormattedMessage
                        id='MarketSpotLight.equitySmartSearchCalculation.baseDownPayment'
                        defaultMessage='Down payment'
                      />
                    )}
                    {percentDown !== TWENTY_PERCENT && (
                      <FormattedMessage
                        id='MarketSpotLight.equitySmartSearchCalculation.suggestedDownPayment'
                        defaultMessage='Sugg. down payment ({percentDown}%)'
                        values={{ percentDown: percentDownAsWholeNumber }}
                      />
                    )}
                  </Text>
                </div>
              </div>
              <div className={classnames(styles.row)}>
                {percentDown === TWENTY_PERCENT && (
                  <>
                    <div className={classnames(styles.column, styles.column1)}>
                      <Text
                        variant='neutralCool'
                        variantCount='700'
                        element='span'
                        size='xxs'
                        weight='semibold'
                      >
                        <IconTimes variant='neutralCool' variantCount='700' />
                      </Text>
                    </div>
                    <div className={classnames(styles.column, styles.column2)}>
                      <Text
                        element='span'
                        variant='neutralCool'
                        variantCount='700'
                      >
                        5
                      </Text>
                    </div>
                    <div className={classnames(styles.column, styles.column3)}>
                      <Text
                        element='span'
                        variant='neutralCool'
                        variantCount='700'
                        size='xs'
                      >
                        <FormattedMessage
                          id='MarketSpotLight.equitySmartSearchCalculation.assumingDownPercentage'
                          defaultMessage='Assuming {percentDown}% down'
                          values={{ percentDown: percentDownAsWholeNumber }}
                        />
                      </Text>
                    </div>
                  </>
                )}
                {percentDown !== TWENTY_PERCENT && (
                  <>
                    <div className={classnames(styles.column, styles.column1)}>
                      <Text
                        variant='neutralCool'
                        variantCount='700'
                        element='span'
                        size='xxs'
                        weight='semibold'
                      >
                        <IconPlus />
                      </Text>
                    </div>
                    <div className={classnames(styles.column, styles.column2)}>
                      <Text
                        element='span'
                        variant='neutralCool'
                        variantCount='700'
                      >
                        <Format.Currency value={loanBalance} />
                      </Text>
                    </div>
                    <div className={classnames(styles.column, styles.column3)}>
                      <Text
                        element='span'
                        variant='neutralCool'
                        variantCount='700'
                      >
                        <FormattedMessage
                          id='MarketSpotLight.equitySmartSearchCalculation.newLoanAmount'
                          defaultMessage='New loan amount'
                        />
                      </Text>
                    </div>
                  </>
                )}
              </div>
              <div className={classnames(styles.row, styles.border)}>
                <div className={classnames(styles.column, styles.column1)} />
                <div className={classnames(styles.column, styles.column2)}>
                  <Text element='span' variant='primary' weight='semibold'>
                    <Format.Currency value={purchasePrice} />
                  </Text>
                </div>
                <div className={classnames(styles.column, styles.column3)}>
                  <Text element='span' variant='primary' weight='semibold'>
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearchCalculation.purchasePrice'
                      defaultMessage='Purchase price'
                    />
                  </Text>
                </div>
              </div>
            </div>
          </li>

          {/* THIRD LIST ITEM */}
          <li className={styles.listItem}>
            {/* Third Blurb */}
            <div className={styles.listHeading}>
              <Text
                element='div'
                size='l'
                align='left'
                variant='neutralCool'
                variantCount='900'
              >
                {mortgagePaymentDifference < 0 && (
                  <FormattedMessage
                    id='MarketSpotLight.equitySmartSearchCalculation.monthlyPaymentDown'
                    defaultMessage='Your monthly payment would likely go down {mortgageDifference} since it’s a smaller loan'
                    values={{
                      mortgageDifference: (
                        <Text
                          element='span'
                          variant='success'
                          variantCount='700'
                          className={styles.paymentWrapper}
                        >
                          <div className={styles.paymentTildeWrapper}>
                            <span
                              className={styles.paymentTilde}
                              aria-hidden='true'
                              title='about'
                            >
                              <IconTilde />
                            </span>
                            <span className={styles.srOnly}>about </span>
                            <Format.Currency
                              value={Math.abs(mortgagePaymentDifference)}
                              abbreviate
                            />
                            /mo
                          </div>
                        </Text>
                      ),
                    }}
                  />
                )}
                {mortgagePaymentDifference >= 0 && (
                  <FormattedMessage
                    id='MarketSpotLight.equitySmartSearchCalculation.monthlyPaymentUp'
                    defaultMessage='Though your monthly payment would likely go up {mortgageDifference}'
                    values={{
                      mortgageDifference: (
                        <Text
                          element='span'
                          variant='danger'
                          className={styles.paymentWrapper}
                        >
                          <span className={styles.paymentTildeWrapper}>
                            <span
                              className={styles.paymentTilde}
                              aria-hidden='true'
                              title='about'
                            >
                              <IconTilde />
                            </span>
                            <span className={styles.srOnly}>about </span>
                            <Format.Currency
                              value={mortgagePaymentDifference}
                              abbreviate
                            />
                            /mo
                          </span>
                        </Text>
                      ),
                    }}
                  />
                )}
              </Text>
              <div className={styles.subSection}>
                <Text
                  element='div'
                  align='left'
                  variant='neutralCool'
                  variantCount='700'
                  size='s'
                >
                  <FormattedMessage
                    id='MarketSpotLight.equitySmartSearchCalculation.thirtyYearRate'
                    defaultMessage='For a 30-year fixed loan at average rates'
                  />
                </Text>
              </div>
            </div>
          </li>
          <li className={styles.listItem}>
            <div className={styles.listHeading}>
              <Text
                element='div'
                size='l'
                align='left'
                variant='neutralCool'
                variantCount='900'
              >
                <FormattedMessage
                  id='MarketSpotLight.equitySmartSearchCalculation.anyQuestions'
                  defaultMessage='Have any questions?'
                />
              </Text>
              <div className={styles.subSection}>
                <Question
                  questionTopic={intlFormat(
                    SYSTEM_REQUEST.equitySmartSearchTableCalculationTopic
                  )}
                  questionTopicKey={EQUITY_SMART_SEARCH_TOPIC_KEY}
                  placeholder={intlFormat(message.placeholder, {
                    firstName,
                  })}
                  avatarUri={avatarUri}
                />
              </div>
            </div>
          </li>
        </ul>
      </Page.Body>
    </Page>
  )
}

export default EquitySmartSearchCalculation
